import React, { useCallback, useEffect, useMemo, useState } from 'react';

import useSittingStore from '../../stores/sittingStore';
import usePatientPlanStore from '../../stores/patientPlanStore';
import { usePermissions, usePermission } from '../../context/PermissionContext';
import { fetchStaffList } from '../../services/api/staffApi';
import { addProcedureNote } from '../../services/api/visitMetadataApi';
import { useVisitMetadata } from '../../hooks/useVisitMetadata';
import useSittingActions from '../../hooks/useSittingActions';
import SittingCard from '../procedures/SittingCard';
import ProcedureNoteThreadPreview from '../procedures/ProcedureNoteThreadPreview';
import AddProcedureModal from '../procedures/AddProcedureModal';
import AttachPlanModal from '../procedures/AttachPlanModal';
import ActionPendingDialog from './ActionPendingDialog';
import { INTENTS } from '../../constants/visitActionPending';

/**
 * VisitProceduresPanel — inline "Procedures" tab content for a single visit.
 *
 * Mounts when VisitActionsPanel's activeAction === 'procedures' (or the
 * nurse / receptionist tab dispatches the same key via useRoleTabs).
 *
 * Reads sittings from useSittingStore().byVisit[visit.visit_id] so WS
 * events patch the list live. On first mount it kicks off fetchForVisit.
 *
 * Action flow mirrors ProcedureQueuePage: the parent owns a small modal
 * prompt for actions that need a reason / amount (collect_payment, reject,
 * cancel); execute/complete/approve go straight through.
 */
export default function VisitProceduresPanel({ visit, viewer, onClose, readOnly = false }) {
  const { hasPermission } = usePermissions();

  const {
    byVisit,
    fetchForVisit,
    fetchVisitNotes,
    isLoading,
    error,
  } = useSittingStore();

  useEffect(() => {
    if (!visit?.visit_id) return;
    fetchForVisit(visit.visit_id);
    // Eager-load all procedure notes for this visit in a single GSI9
    // Query so the inline preview under each SittingCard renders
    // immediately and opening the dialog hits a warm cache.
    fetchVisitNotes(visit.visit_id);
  }, [visit?.visit_id, fetchForVisit, fetchVisitNotes]);

  const sittings = useMemo(() => byVisit[visit?.visit_id] || [], [byVisit, visit?.visit_id]);

  // Staff list for the assignee picker (Add Procedure + Reassign). We
  // fetch once per panel mount; the list changes rarely enough that
  // this is cheaper than threading a shared store through.
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await fetchStaffList();
      if (!cancelled && r.success) setStaff(r.data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  const doctors = useMemo(
    () => (staff || []).filter((m) => (m.role || '').toUpperCase() === 'DOCTOR'),
    [staff],
  );
  const nurses = useMemo(
    () => (staff || []).filter((m) => (m.role || '').toUpperCase() === 'NURSE'),
    [staff],
  );

  const [addOpen, setAddOpen] = useState(false);
  // Phase 2.7 — workflow plan attach modal
  const [attachOpen, setAttachOpen] = useState(false);
  // Phase 6.0 / 7 — auto-close UX fix. After a doctor attaches a plan,
  // open the action-pending dialog pre-filled to send the visit to
  // Reception for payment, instead of teleporting to a new tab. Only
  // applies when the viewer is the doctor who's parking the visit.
  const [postAttachDialogOpen, setPostAttachDialogOpen] = useState(false);
  const isDoctor = (viewer?.role || '').toUpperCase() === 'DOCTOR';

  const reload = useCallback(() => {
    if (!visit?.visit_id) return;
    fetchForVisit(visit.visit_id);
    fetchVisitNotes(visit.visit_id);
  }, [visit?.visit_id, fetchForVisit, fetchVisitNotes]);

  // Phase 1f — route all sitting actions through the shared hook.
  // Eliminates the bespoke handleAction + PromptDialog + ReassignDialog
  // + ApprovalReviewDialog wiring (now owned by the hook) and guarantees
  // the Collect Payment button flip lands via the same
  // patchAcrossProjections path ProcedureQueuePage uses.
  const { handleAction, dialogs } = useSittingActions({
    viewer,
    onAfterAction: reload,
  });

  // R5 (owner) — readOnly suppresses every write trigger on this panel:
  // Add procedure, Attach plan, every SittingCard action, and the
  // From-plan "Start" button. Existing sittings/notes still show.
  const canAdd = hasPermission('ADD_PROCEDURE') && !readOnly;
  const canDraftPlan = hasPermission('DRAFT_TREATMENT_PLAN') && !readOnly;

  // Phase 2.5 — build a plan-title lookup so each SittingCard can show
  // WHICH plan it belongs to. Reads from activePlansByPatient which is
  // already hydrated by ActivePlansSummaryStrip below.
  const activePlansByPatient = usePatientPlanStore(
    (s) => s.activePlansByPatient,
  );
  const planTitleById = useMemo(() => {
    const map = {};
    const plans = (visit?.patient_id && activePlansByPatient[visit.patient_id]) || [];
    for (const pd of plans) {
      const plan = pd?.plan;
      if (plan?.plan_id) map[plan.plan_id] = plan.plan_title || '';
    }
    return map;
  }, [visit?.patient_id, activePlansByPatient]);

  return (
    <div className="p-3 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100">
      {/* Heading bar has its own solid background so it stays readable
          even when the containing wrapper renders translucent (see the
          Phase 2.5 contrast rule in frontend CLAUDE.md). */}
      <div className="flex items-center justify-between mb-3 px-3 py-2 rounded-md border-2 border-indigo-400 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/50">
        <div>
          <h3 className="text-base font-bold text-indigo-900 dark:text-indigo-100">
            🩺 Procedures for this visit
          </h3>
          <p className="text-xs font-medium text-indigo-800 dark:text-indigo-300">
            {sittings.length} sitting{sittings.length === 1 ? '' : 's'}
            {isLoading ? ' · loading…' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {canAdd && (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="text-sm px-3 py-1.5 rounded-md bg-teal-600 text-white hover:bg-teal-700"
            >
              + Add procedure
            </button>
          )}
          {canDraftPlan && (
            <button
              type="button"
              onClick={() => setAttachOpen(true)}
              className="text-sm font-medium px-3 py-1.5 rounded-md border border-indigo-400 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/40"
              title="Attach a reusable workflow plan to this visit"
            >
              📘 Attach plan
            </button>
          )}
          <button
            type="button"
            onClick={reload}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing…' : 'Refresh'}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}

      <ActivePlansSummaryStrip visit={visit} />
      <FromPlanSection
        visit={visit}
        visitSittings={sittings}
        onAfterStart={reload}
      />

      {sittings.length === 0 ? (
        <div className="text-sm text-slate-500 dark:text-slate-400 italic py-4 text-center border border-dashed border-slate-300 dark:border-slate-600 rounded-md">
          No procedures on this visit yet.
          {canAdd && ' Click "Add procedure" to create one.'}
        </div>
      ) : (
        <div className="space-y-3">
          {sittings.map((s) => (
            <div key={s.sitting_id} className="space-y-2">
              <SittingCard
                sitting={s}
                viewer={viewer}
                onAction={handleAction}
                dense
                planTitle={planTitleById[s.plan_id]}
              />
              <ProcedureNoteThreadPreview
                sitting={s}
                viewer={viewer}
              />
              <ProcedureNoteSection
                sitting={s}
                visit={visit}
                viewer={viewer}
              />
            </div>
          ))}
        </div>
      )}

      <AddProcedureModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        visit={visit}
        viewer={viewer}
        doctors={doctors}
        nurses={nurses}
        onCreated={reload}
      />

      <AttachPlanModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        visit={visit}
        viewer={viewer}
        // Phase 6.0 / 7 — for doctors, suppress the legacy new-tab
        // navigate and instead chain into the action-pending dialog so
        // they explicitly hand the visit over to Reception. Other roles
        // keep legacy behavior.
        suppressPostAttachNavigation={isDoctor}
        onAttached={() => {
          reload();
          if (isDoctor) setPostAttachDialogOpen(true);
        }}
      />

      {postAttachDialogOpen && (
        <ActionPendingDialog
          visit={visit}
          viewer={viewer}
          defaultToRole="RECEPTIONIST"
          defaultIntent={INTENTS.ASSIGN_BACK_TO_ME}
          onSubmitted={() => setPostAttachDialogOpen(false)}
          onCancel={() => setPostAttachDialogOpen(false)}
        />
      )}

      {/* Phase 1f — dialogs rendered by the shared hook
          (prompt / reassign / approval review / procedure note). */}
      {dialogs}
    </div>
  );
}




function ProcedureNoteSection({ sitting, visit, viewer, readOnly = true }) {
  // Phase 1e — visit-page view of procedure notes. Read-only by default:
  // notes are now WRITTEN from the Procedure Queue via
  // ProcedureNoteDialog; the visit page is the canonical read surface.
  // Pass `readOnly={false}` to opt back into inline write (unused in 1e;
  // kept for Phase 2 visit-page workflows).
  const { metadata, refresh } = useVisitMetadata(visit?.visit_id);

  const notes = useMemo(
    () => (metadata || []).filter(
      (m) => m.meta_type === 'PROCEDURE_NOTE'
        && m.linked_sitting_id === sitting.sitting_id,
    ),
    [metadata, sitting.sitting_id],
  );

  const viewerMemberId = viewer?.memberId;
  const assigneeMemberId = sitting.assigned_to_member_id || sitting.assigned_to?.member_id;
  const viewerIsAssignee = viewerMemberId && viewerMemberId === assigneeMemberId;
  const viewerIsAdmin = (viewer?.role || '').toUpperCase() === 'ADMIN';
  const isTerminal = ['completed', 'paid', 'cancelled'].includes(sitting.status);
  const canWrite = !readOnly && !isTerminal && (viewerIsAssignee || viewerIsAdmin);

  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Empty + can't write anyway → skip entirely.
  if (!canWrite && notes.length === 0) return null;

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (trimmed.length < 1) return;
    setBusy(true);
    setError('');
    try {
      await addProcedureNote(visit.visit_id, {
        content: trimmed,
        linked_sitting_id: sitting.sitting_id,
      });
      setContent('');
      refresh();
    } catch (err) {
      setError(err?.message || 'Could not save procedure note.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ml-2 pl-3 border-l-2 border-slate-200 dark:border-slate-700">
      {notes.length > 0 && (
        <div className="mb-2 space-y-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Procedure notes ({notes.length})
          </div>
          {notes.map((n) => (
            <div
              key={n.meta_id}
              className="text-sm text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/50 rounded-md px-3 py-2"
            >
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                {n.added_by_name}
                {n.added_by_role ? ` · ${n.added_by_role}` : ''}
              </div>
              <div className="whitespace-pre-wrap">{n.content}</div>
            </div>
          ))}
        </div>
      )}

      {canWrite && (
        <form onSubmit={submit} className="flex gap-2 items-start">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={2}
            placeholder="Add a procedure note (visible to the approver)…"
            className="flex-1 px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            disabled={busy}
          />
          <button
            type="submit"
            disabled={busy || content.trim().length === 0}
            className="px-3 py-1.5 text-sm rounded-md bg-teal-600 hover:bg-teal-700 text-white disabled:bg-teal-300 whitespace-nowrap"
          >
            {busy ? 'Saving…' : 'Add note'}
          </button>
        </form>
      )}

      {error && (
        <div className="mt-1 text-xs text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800 rounded p-1.5">
          {error}
        </div>
      )}
    </div>
  );
}


// Phase 1f — PromptDialog moved to `useSittingActions` (single source of truth).


/**
 * ActivePlansSummaryStrip (Phase 2.5) — compact per-plan header rendered
 * above FromPlanSection. Shows title, progress, estimate/collected, and a
 * Cancel button for users with CANCEL_TREATMENT_PLAN. This is the new
 * home of the cancel UX (moved from PatientHistory in Phase 2.5).
 */
function ActivePlansSummaryStrip({ visit }) {
  const canView = usePermission('VIEW_TREATMENT_PLAN');
  const canCancel = usePermission('CANCEL_TREATMENT_PLAN');

  const activePlansByPatient = usePatientPlanStore(
    (s) => s.activePlansByPatient,
  );
  const fetchActivePlansForPatient = usePatientPlanStore(
    (s) => s.fetchActivePlansForPatient,
  );
  const cancelPlan = usePatientPlanStore((s) => s.cancelPlan);
  const isCancelling = usePatientPlanStore((s) => s.isCancelling);

  const patientId = visit?.patient_id;

  useEffect(() => {
    if (!canView || !patientId) return;
    fetchActivePlansForPatient(patientId).catch(() => {});
  }, [canView, patientId, fetchActivePlansForPatient]);

  const plans = useMemo(
    () => (patientId && activePlansByPatient[patientId]) || [],
    [patientId, activePlansByPatient],
  );

  const onCancel = useCallback(
    async (planId) => {
      const reason = window.prompt(
        'Reason for cancelling this treatment plan (min 3 chars):',
      );
      if (!reason || reason.trim().length < 3) return;
      const result = await cancelPlan(planId, { reason: reason.trim() });
      if (!result.success) {
        window.alert(result.error || 'Could not cancel plan.');
        return;
      }
      if (result.data?.refund_liability_flag) {
        window.alert(
          'Note: completed sittings on this plan have already been paid. '
          + 'Review the collected amount and handle refund offline if owed.',
        );
      }
    },
    [cancelPlan],
  );

  if (!canView || !patientId || !plans.length) return null;

  return (
    <div className="mb-3 flex flex-col gap-1.5">
      {plans.map((pd) => {
        const plan = pd.plan || {};
        const totalItems = (pd.items || []).length;
        const consumed = (pd.items || []).filter(
          (it) => it.status === 'consumed',
        ).length;
        const estimate = Number(plan.total_cost_snapshot || 0);
        const collected = Number(plan.collected_to_date || 0);
        return (
          <div
            key={plan.plan_id}
            className="flex flex-wrap items-center gap-3 text-sm px-3 py-2 rounded-md border-2 border-indigo-400 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/40 shadow-sm"
          >
            <span className="font-bold text-indigo-900 dark:text-indigo-100">
              {plan.plan_title || `Plan ${plan.plan_id?.slice(-6)}`}
            </span>
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-200 dark:bg-indigo-800 text-indigo-900 dark:text-indigo-100">
              {consumed} / {totalItems} done
            </span>
            <span className="font-medium text-slate-900 dark:text-slate-100">
              ₹{collected.toLocaleString('en-IN')}
              <span className="font-normal text-slate-600 dark:text-slate-400">
                {' '}/ ₹{estimate.toLocaleString('en-IN')}
              </span>
            </span>
            <span className="flex-1" />
            {canCancel && (
              <button
                type="button"
                onClick={() => onCancel(plan.plan_id)}
                disabled={isCancelling}
                className="text-xs font-medium px-2.5 py-1 rounded-md border border-red-400 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-60"
              >
                Cancel plan
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}


/**
 * FromPlanSection (Phase 2) — surfaces any active treatment plans for the
 * visit's patient and offers one-click "Start" on each open PlanItem.
 *
 * Renders nothing when:
 *   - The viewer cannot view plans (permission gate).
 *   - The patient has no non-terminal plans.
 *   - All open items are already linked to a sitting on this visit.
 */
function FromPlanSection({ visit, visitSittings, onAfterStart }) {
  const canViewPlan = usePermission('VIEW_TREATMENT_PLAN');
  const canAdd = usePermission('ADD_PROCEDURE');

  const activePlansByPatient = usePatientPlanStore(
    (s) => s.activePlansByPatient,
  );
  const fetchActivePlansForPatient = usePatientPlanStore(
    (s) => s.fetchActivePlansForPatient,
  );
  const startVisitSittingForItem = usePatientPlanStore(
    (s) => s.startVisitSittingForItem,
  );

  const patientId = visit?.patient_id;

  useEffect(() => {
    if (!canViewPlan || !patientId) return;
    fetchActivePlansForPatient(patientId).catch(() => {});
  }, [canViewPlan, patientId, fetchActivePlansForPatient]);

  const plans = useMemo(
    () => (patientId && activePlansByPatient[patientId]) || [],
    [patientId, activePlansByPatient],
  );

  // Item IDs already linked to a sitting bound to this visit — filter them out.
  const linkedItemIds = useMemo(() => {
    const set = new Set();
    for (const s of visitSittings || []) {
      if (s.item_id) set.add(s.item_id);
    }
    return set;
  }, [visitSittings]);

  // Item IDs that have a live (non-cancelled) sitting anywhere in the
  // plan — possibly on a different visit. We hide these so the doctor
  // can't start a duplicate sitting; the backend would reject it anyway.
  const livelyLinkedItemIds = useMemo(() => {
    const set = new Set();
    for (const pd of plans) {
      for (const s of pd.sittings || []) {
        if (s.item_id && s.status && s.status !== 'cancelled') {
          set.add(s.item_id);
        }
      }
    }
    return set;
  }, [plans]);

  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState('');

  const onStart = useCallback(
    async (plan_id, item_id) => {
      if (!visit?.visit_id) return;
      const key = `${plan_id}|${item_id}`;
      setBusyKey(key);
      setError('');
      try {
        const result = await startVisitSittingForItem(plan_id, item_id, {
          visit_id: visit.visit_id,
        });
        if (!result.success) {
          setError(result.error || 'Could not start procedure for this item.');
        } else if (onAfterStart) {
          onAfterStart();
        }
      } finally {
        setBusyKey(null);
      }
    },
    [visit?.visit_id, startVisitSittingForItem, onAfterStart],
  );

  if (!canViewPlan || !patientId || !plans.length) return null;

  // Flatten to items the doctor can start on THIS visit:
  //   - status=open (not consumed/cancelled)
  //   - no sitting already on this visit
  //   - no live (non-cancelled) sitting on any OTHER visit
  const rows = [];
  for (const pd of plans) {
    const plan = pd.plan || {};
    for (const item of pd.items || []) {
      const status = item.status || 'open';
      if (status !== 'open') continue;
      if (linkedItemIds.has(item.item_id)) continue;
      if (livelyLinkedItemIds.has(item.item_id)) continue;
      rows.push({ plan, item });
    }
  }
  if (!rows.length) return null;

  return (
    <div className="mb-3 border-2 border-indigo-400 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/40 rounded-md shadow-sm">
      <div className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-indigo-800 dark:text-indigo-200 border-b border-indigo-300 dark:border-indigo-700 bg-indigo-100 dark:bg-indigo-900/60">
        From treatment plan · {rows.length} open
      </div>
      <div className="px-3 py-2 space-y-1.5">
        {rows.map(({ plan, item }) => {
          const key = `${plan.plan_id}|${item.item_id}`;
          const cost = Number(item.final_cost || 0);
          return (
            <div
              key={key}
              className="flex items-center gap-2 text-sm text-slate-900 dark:text-slate-100 font-medium"
            >
              <span className="flex-1 truncate">
                {item.procedure_snapshot?.name || 'Procedure'}
                {plan.plan_title && (
                  <span className="text-xs font-normal text-slate-600 dark:text-slate-400 ml-1">
                    · {plan.plan_title}
                  </span>
                )}
              </span>
              <span className="font-bold text-sm text-slate-900 dark:text-slate-100">
                ₹{cost.toLocaleString('en-IN')}
              </span>
              {canAdd && (
                <button
                  type="button"
                  onClick={() => onStart(plan.plan_id, item.item_id)}
                  disabled={busyKey === key}
                  className="text-xs font-medium px-2.5 py-1 rounded-md border border-indigo-500 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 disabled:opacity-60"
                >
                  {busyKey === key ? 'Starting…' : 'Start'}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {error && (
        <div className="px-3 pb-2 text-xs text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
    </div>
  );
}
